/**
 * Acceso: modal de login/registro de la landing y ayudas de los formularios.
 *
 * En cualquier página:
 *   [data-ver-clave]   Botón «Mostrar/Ocultar» junto a un [data-clave].
 *   [data-fuerza]      Cuatro tramos que se colorean según la contraseña de
 *                      [data-fuerza-origen], con la misma regla que el servidor.
 *
 * Solo donde existe [data-auth-dialog] (la landing):
 *   [data-auth-modal="login|register"]  Enlaces que, en vez de navegar a
 *                      /login o /register, abren el modal en esa pestaña.
 *   #login y #registro en la URL abren el modal al cargar.
 *   Los formularios del modal se envían por fetch a las rutas de siempre.
 *   Laravel responde 422 con los errores en JSON; si todo va bien sigue la
 *   redirección, y aquí se navega a donde ha llevado (login) o se muestra el
 *   acuse (registro).
 */

const COLORES_FUERZA = ['', '#E5484D', '#F5A524', '#22C7B8', '#0F9E90'];

function puntuarClave(clave) {
    return (clave.length >= 8) + /[A-Z]/.test(clave) + /[a-z]/.test(clave) + /\d/.test(clave);
}

function initAyudasClave(raiz = document) {
    raiz.querySelectorAll('[data-ver-clave]').forEach((boton) => {
        const campo = boton.parentElement.querySelector('[data-clave]');
        boton.addEventListener('click', () => {
            const ver = campo.type === 'password';
            campo.type = ver ? 'text' : 'password';
            boton.textContent = ver ? 'Ocultar' : 'Mostrar';
            boton.setAttribute('aria-pressed', ver ? 'true' : 'false');
        });
    });

    raiz.querySelectorAll('[data-fuerza]').forEach((barra) => {
        const campo = barra.closest('form').querySelector('[data-fuerza-origen]');
        const tramos = [...barra.children];
        const pintar = () => {
            const clave = campo.value;
            const puntos = clave ? Math.max(1, puntuarClave(clave)) : 0;
            tramos.forEach((tramo, i) => {
                tramo.style.backgroundColor = i < puntos ? COLORES_FUERZA[puntos] : '';
            });
        };
        campo.addEventListener('input', pintar);
        pintar();
    });
}

function initModal() {
    const dialogo = document.querySelector('[data-auth-dialog]');
    if (!dialogo) {
        return;
    }

    const pestanas = [...dialogo.querySelectorAll('[data-auth-pestana]')];
    const paneles = [...dialogo.querySelectorAll('[data-auth-panel]')];
    const hecho = dialogo.querySelector('[data-auth-hecho]');

    function mostrar(cual) {
        pestanas.forEach((p) => p.setAttribute('aria-selected', p.dataset.authPestana === cual ? 'true' : 'false'));
        paneles.forEach((panel) => {
            panel.hidden = panel.dataset.authPanel !== cual;
        });
        if (hecho) {
            hecho.hidden = true;
        }
        dialogo.setAttribute('aria-label', cual === 'login' ? 'Iniciar sesión' : 'Crear cuenta');
    }

    function abrir(cual) {
        if (!paneles.some((panel) => panel.dataset.authPanel === cual)) {
            return false;
        }
        mostrar(cual);
        if (!dialogo.open) {
            dialogo.showModal();
        }
        const primero = dialogo.querySelector(`[data-auth-panel="${cual}"] input:not([type=hidden])`);
        if (primero) {
            primero.focus();
        }
        return true;
    }

    function cerrar() {
        dialogo.close();
    }

    // Delegado: también cubre los enlaces de dentro del propio modal.
    document.addEventListener('click', (evento) => {
        const enlace = evento.target.closest('[data-auth-modal]');
        if (enlace && abrir(enlace.dataset.authModal)) {
            evento.preventDefault();
        }
    });

    pestanas.forEach((p) => p.addEventListener('click', () => abrir(p.dataset.authPestana)));
    dialogo.querySelectorAll('[data-auth-cerrar]').forEach((b) => b.addEventListener('click', cerrar));

    // Clic en el fondo (fuera de la tarjeta) cierra.
    dialogo.addEventListener('click', (evento) => {
        if (evento.target === dialogo) {
            cerrar();
        }
    });

    dialogo.querySelectorAll('[data-auth-form]').forEach((formulario) => enviarPorFetch(formulario, dialogo));

    const ancla = { '#login': 'login', '#registro': 'register' }[window.location.hash];
    if (ancla) {
        abrir(ancla);
    }
}

function pintarErrores(formulario, mensajes, sinConfirmar = false) {
    const caja = formulario.querySelector('[data-auth-error]');
    caja.replaceChildren(...mensajes.map((texto) => {
        const linea = document.createElement('span');
        linea.textContent = texto;
        return linea;
    }));
    caja.hidden = mensajes.length === 0;

    // El login solo avisa de la cuenta sin confirmar cuando la contraseña era
    // correcta (FrontLoginRequest), que es justo cuando tiene sentido ofrecer
    // el reenvío del correo. En cualquier otro fallo, dejarlo oculto.
    //
    // El bloque de reenvío va dentro del propio formulario de login, justo
    // debajo de la caja de error, así que se busca con querySelector sobre el
    // formulario (no hace falta ningún contenedor intermedio).
    const bloque = formulario.querySelector('[data-auth-reenviar-login]');
    if (bloque) {
        bloque.hidden = !sinConfirmar;

        // Se reutiliza tal cual lo que el usuario acaba de escribir. El
        // endpoint lo acepta como email o como usuario, así que no hay que
        // distinguir los dos casos ni volver a preguntárselo
        // (ver FrontResendVerificationController).
        const escrito = formulario.querySelector('input[name="login"]').value.trim();
        const campo = bloque.querySelector('[data-auth-reenviar-email]');
        const destino = bloque.querySelector('[data-auth-reenviar-destino]');

        if (campo && escrito) {
            campo.value = escrito;
        }
        // El texto llega ya traducido en data-plantilla: este JS no lleva
        // ningún literal, porque el front es multiidioma.
        if (destino && escrito) {
            destino.textContent = destino.dataset.plantilla.replace(':login', escrito);
        }
    }
}

function enviarPorFetch(formulario, dialogo) {
    const boton = formulario.querySelector('button[type="submit"]');
    const etiqueta = formulario.querySelector('[data-auth-etiqueta]');
    // Sin etiqueta el envío funciona igual, solo se pierde el texto de
    // "Entrando…". Antes esto reventaba con un TypeError y dejaba el modal
    // entero sin poder enviarse.
    const textoBoton = etiqueta ? etiqueta.textContent : null;
    const tipo = formulario.dataset.authForm;

    formulario.addEventListener('submit', async (evento) => {
        evento.preventDefault();
        if (boton.disabled) {
            return;
        }

        pintarErrores(formulario, []);
        boton.disabled = true;
        if (etiqueta) {
            etiqueta.textContent = etiqueta.dataset.enviando;
        }

        try {
            const respuesta = await fetch(formulario.action, {
                method: 'POST',
                body: new FormData(formulario),
                headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                credentials: 'same-origin',
            });

            if (respuesta.status === 422) {
                const datos = await respuesta.json();
                pintarErrores(
                    formulario,
                    Object.values(datos.errors || {}).flat(),
                    Boolean(datos.errors && datos.errors.login_unconfirmed)
                );
                return;
            }

            if (respuesta.status === 419) {
                pintarErrores(formulario, ['La sesión ha caducado. Recarga la página y vuelve a intentarlo.']);
                return;
            }

            if (!respuesta.ok) {
                pintarErrores(formulario, ['No hemos podido completar la operación. Inténtalo de nuevo en unos minutos.']);
                return;
            }

            // Registro sin autoconfirmación: el servidor ha llevado a
            // /register/saved, que ya ha enviado el correo de confirmación.
            // Se muestra el acuse en el modal en vez de navegar allí.
            if (tipo === 'register' && new URL(respuesta.url).pathname.endsWith('/register/saved')) {
                mostrarAcuseRegistro(formulario, dialogo);
                return;
            }

            // Login (o registro autoconfirmado): se va a donde ha redirigido
            // el servidor, que puede ser la página pedida o el segundo factor.
            window.location.assign(respuesta.url);
        } catch {
            pintarErrores(formulario, ['No hay conexión. Revisa tu red y vuelve a intentarlo.']);
        } finally {
            boton.disabled = false;
            if (etiqueta) {
                etiqueta.textContent = textoBoton;
            }
        }
    });
}

/**
 * Acuse del registro: el modal no navega a /register/saved, así que el aviso
 * del correo y el botón de reenviar tienen que salir de aquí. Es la única
 * pantalla donde el usuario recién registrado ve su email, porque el
 * formulario se vacía justo después.
 *
 * El envío del reenvío lo lleva el script inline del propio modal
 * (front_auth_modal.blade.php, @push('foot_page')); aquí solo se le deja el
 * email en data-email para que ese script lo encuentre al pulsar.
 */
function mostrarAcuseRegistro(formulario, dialogo) {
    const hecho = dialogo.querySelector('[data-auth-hecho]');
    const nombre = formulario.querySelector('[data-auth-nombre]').value.trim().split(' ')[0];

    dialogo.querySelectorAll('[data-auth-panel]').forEach((panel) => {
        panel.hidden = true;
    });
    hecho.querySelector('[data-auth-hecho-titulo]').textContent = nombre ? `Bienvenido, ${nombre}` : 'Bienvenido';
    hecho.hidden = false;

    // El email se lee antes de reset(): es lo que necesita el reenvío.
    const email = formulario.querySelector('input[name="email"]').value;
    formulario.reset();

    const boton = hecho.querySelector('[data-auth-reenviar]');
    if (boton) {
        boton.dataset.email = email;
    }
}

/**
 * Desplegable "Mi cuenta" de la cabecera y el pie (nombre del usuario ->
 * Perfil/Administración/Cerrar sesión), en cualquier página con sesión
 * iniciada. Contenido en menu navbar-right, ver HeaderFooterMenuSeeder.
 */
function initAccountMenu() {
    document.querySelectorAll('[data-account-menu]').forEach((menu) => {
        const boton = menu.querySelector('[data-account-trigger]');
        const panel = menu.querySelector('[data-account-panel]');
        if (!boton || !panel) {
            return;
        }

        const cerrar = () => {
            panel.hidden = true;
            boton.setAttribute('aria-expanded', 'false');
        };

        boton.addEventListener('click', (evento) => {
            evento.stopPropagation();
            const abierto = !panel.hidden;
            document.querySelectorAll('[data-account-panel]').forEach((otro) => {
                otro.hidden = true;
            });
            panel.hidden = abierto;
            boton.setAttribute('aria-expanded', abierto ? 'false' : 'true');
        });

        document.addEventListener('click', (evento) => {
            if (!panel.hidden && !menu.contains(evento.target)) {
                cerrar();
            }
        });

        document.addEventListener('keydown', (evento) => {
            if (evento.key === 'Escape' && !panel.hidden) {
                cerrar();
                boton.focus();
            }
        });
    });
}

function boot() {
    initAyudasClave();
    initModal();
    initAccountMenu();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
    boot();
}
